import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { fadeUp } from "../animations/variants";
import { publicClient } from "../configs/axiosConfig"; // ✅ direct, no circular deps
import { Eye, Globe } from "lucide-react";
import { CreditCard, Wallet, Smartphone, Banknote } from "lucide-react";
import { useSelector } from "react-redux";
import { footerText } from "../i18n/footer";
import { Facebook, Instagram } from "lucide-react";
import {
  FaCcVisa,
  FaCcMastercard,
  FaApplePay,
  FaGooglePay,
  FaStripe,
} from "react-icons/fa";
import { facebooklink, instagramlink } from "./SocialMediaLink";

const Footer = () => {
  const [gaStats, setGaStats] = useState(null);

  const lang = useSelector((state) => state.language.lang);
  const t = footerText[lang];

  // useEffect(() => {
  //   let mounted = true;

  //   const fetchGaStats = async () => {
  //     try {
  //       const res = await publicClient.get("v2/statistic/ga-visitors");
  //       if (mounted) {
  //         setGaStats(res.data);
  //       }
  //     } catch (err) {
  //       console.error("Footer GA fetch failed:", err);
  //     }
  //   };

  //   fetchGaStats();

  //   return () => {
  //     mounted = false;
  //   };
  // }, []);

  const normalizeCount = (value) => {
    if (typeof value !== "number") return 100;
    return value + 100;
  };

  const normalizeCountryName = (country) => {
    if (!country || country.trim() === "") return "Unknown";
    return country;
  };

  const COUNTRY_CODE_MAP = {
    "New Zealand": "NZ",
    Australia: "AU",
    Vietnam: "VN",

    "United States": "US",
    "United Kingdom": "GB",
    Canada: "CA",

    China: "CN",
    Japan: "JP",
    "South Korea": "KR",
    Thailand: "TH",
    Singapore: "SG",
    Malaysia: "MY",
    Indonesia: "ID",
    Philippines: "PH",

    India: "IN",

    France: "FR",
    Germany: "DE",
    Netherlands: "NL",
    Spain: "ES",
    Italy: "IT",

    Brazil: "BR",
    Mexico: "MX",
    "South Africa": "ZA",
  };

  const isoToFlag = (isoCode) => {
    if (!isoCode || isoCode.length !== 2) return "🌐";

    return isoCode
      .toUpperCase()
      .replace(/./g, (char) =>
        String.fromCodePoint(127397 + char.charCodeAt()),
      );
  };

  const countryToFlag = (country) => {
    if (!country || country.trim() === "") return "🌐";

    const iso = COUNTRY_CODE_MAP[country] || country.slice(0, 2).toUpperCase();

    return isoToFlag(iso);
  };

  const countryToCode = (country) => {
    if (!country || country.trim() === "") return "UN";

    return COUNTRY_CODE_MAP[country] || country.slice(0, 2).toUpperCase();
  };

  useEffect(() => {
    let mounted = true;
    publicClient
      .get("v2/statistic/ga-visitors")
      .then((res) => mounted && setGaStats(res.data))
      .catch(() => {});
    return () => (mounted = false);
  }, []);

  const normalize = (v) => (typeof v === "number" ? v + 100 : 100);

  return (
    <footer className="bg-dry text-textGray py-12 px-6">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="max-w-6xl mx-auto px-0 py-16"
      >
        {/* TOP GRID */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* BRAND */}
          <div>
            <h3 className="font-semibold font-heading text-neonYellow animate-neonFlicker">
              SAIGON STAR
            </h3>
            <p className="mt-3 text-sm text-darkbg max-w-xs">
              SATISFYING YOUR VIETNAMESE CRAVINGS SINCE 2005
            </p>

            <div className="flex items-center gap-4 mt-6 text-neonYellow animate-neonFlicker">
              <a
                href={facebooklink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="transition hover:text-darkbg"
              >
                <Facebook className="w-5 h-5" />
              </a>

              <a
                href={instagramlink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="transition hover:text-neonYellow"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* INFO */}
          <div>
            <h4 className="font-semibold text-darkbg mb-4">Info</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="/search" className="hover:text-neonYellow">
                  Search
                </a>
              </li>
              <li>
                <a href="/about" className="hover:text-neonYellow">
                  Our Story
                </a>
              </li>
              <li>
                <a href="/contact" className="hover:text-neonYellow">
                  Contact Us
                </a>
              </li>
            </ul>
          </div>

          {/* SUBSCRIBE */}
          <div>
            <h4 className="font-semibold text-darkbg mb-4">Subscribe</h4>
            <p className="text-sm text-gray-500 mb-4">
              Sign up to our mailing list
            </p>

            <div className="flex items-center">
              <input
                type="email"
                placeholder="Your email"
                className="w-full px-4 py-2 border border-yellowstar rounded-l-md text-sm focus:outline-none focus:border-yellowstar"
              />
              <button className="px-4 py-2 bg-dry text-yellowstar text-sm rounded-r-md hover:bg-yellowstar hover:text-dry border border-yellowstar">
                OK
              </button>
            </div>
          </div>
        </div>

        {/* DIVIDER */}
        <div className="border-t border-border mt-14 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          {/* LEFT */}
          <div className="flex flex-col lg:items-start items-center gap-3">
            <p className="text-xs text-gray-500 text-center">
              © {new Date().getFullYear()} SAIGON STAR. {t.copyright}
            </p>
            <p className="text-xs text-gray-500 text-center">
              {t.designBy}{" "}
              <a
                href="https://miraelite.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neonYellow animate-neonFlicker  "
              >
                Mira Elite
              </a>{" "}
              {t.withLove}
            </p>
          </div>

          {/* RIGHT – PAYMENT */}
          <div className="flex flex-col items-center lg:items-end gap-3 text-gray-400 mt-10 lg:mt-0">
            <div className="flex items-center gap-3">
              <div className="h-7 min-w-[48px] px-2 flex items-center justify-center rounded-md border border-gray-600 text-xs font-semibold tracking-widest text-gray-600">
                VISA
              </div>

              <div className="px-2 py-0 border border-gray-600 rounded ">
                <FaCcMastercard className="w-6 h-6 text-gray-600" />
              </div>
              <div className="flex items-center gap-1 px-2 py-0 border border-gray-600 rounded ">
                <FaStripe className="w-6 h-6 text-gray-600" title="Stripe" />
              </div>
              {/* Apple Pay box */}
              <div className="flex items-center gap-1 px-2 py-0 border border-gray-600 rounded ">
                <FaApplePay className="w-6 h-6 text-gray-600" />
              </div>

              {/* Google Pay box */}
              <div className="flex items-center gap-1 px-2 py-0 border border-gray-600 rounded ">
                <FaGooglePay className="w-6 h-6 text-gray-600" />
              </div>
            </div>

            {gaStats && (
              <div className="mb-0 flex flex-col items-center gap-1 text-xs text-textGray">
                {/* MAIN LINE */}
                <div className="flex items-center justify-center gap-3">
                  <div className="flex items-center gap-1">
                    <Eye size={14} className="text-gray-500" />
                    <span className="text-neonYellow animate-neonFlicker">
                      {normalizeCount(gaStats.realtime?.totalUsers)}
                    </span>
                    <span className="text-gray-500">{t.online}</span>
                  </div>

                  <span className="text-gray-500">•</span>

                  <div className="flex items-center gap-1">
                    <Globe size={14} className="text-gray-500" />
                    <span className="text-neonYellow animate-neonFlicker">
                      {normalizeCount(gaStats.allTime?.totalUsers)}
                    </span>
                    <span className="text-gray-500">{t.visitors}</span>
                  </div>

                  {gaStats.allTime?.byCountry?.length > 0 && (
                    <>
                      <span className="text-gray-500">•</span>

                      <div className="text-[11px] text-gray-500">
                        {" "}
                        {gaStats?.allTime?.byCountry
                          .slice(0, 3)
                          .map((c) => {
                            const countryName = normalizeCountryName(c.country);
                            const code = countryToCode(countryName);

                            return `${countryToFlag(c.country)} ${normalizeCount(
                              c.users,
                            )}`;
                          })
                          .join(" · ")}
                      </div>
                    </>
                  )}
                </div>

                {/* BREAKDOWN */}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
